import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import Global from '../../../store/global-selectors';

import ConnectedDevice from './connected-device';
import style from './style.module.less';
import messages from './messages';
import { PRINTER, SCALE, DIMENSIONALIZER } from './constants';
import { emptyArray } from 'ui-components/default-props/empty';

const ConnectedDevices = ({
  workstationId,
  openModal,
  userId,
  hasAdminOrConfigPermissions = false,
  currentUserWorkstationIds = emptyArray,
}) => {
  const printers = useSelector(Global.selectConnectPrinters);
  const workstationPrinters = useMemo(() => {
    const flattenedPrinters = printers.reduce(
      (flattened, p) => (p.workstationId === workstationId ? [...flattened, ...p.printers] : flattened),
      [],
    );
    if (hasAdminOrConfigPermissions || currentUserWorkstationIds.find((id) => id === workstationId)) {
      return flattenedPrinters;
    }
    return flattenedPrinters.filter((printer) => printer.shared === true);
  }, [printers, currentUserWorkstationIds, hasAdminOrConfigPermissions, workstationId]);

  const scales = useSelector(Global.selectConnectScales);
  const getScaleType = (scale) => (scale.isDimensionalizer ? DIMENSIONALIZER : SCALE);

  const workstationScales = useMemo(() => {
    const flattenedScales = scales.reduce(
      (flattened, s) => (s.workstationId === workstationId ? [...flattened, ...s.scales] : flattened),
      [],
    );
    if (hasAdminOrConfigPermissions || currentUserWorkstationIds.find((id) => id === workstationId)) {
      return flattenedScales;
    }
    return flattenedScales.filter((scale) => scale.shared === true);
  }, [scales, currentUserWorkstationIds, hasAdminOrConfigPermissions, workstationId]);

  return (
    <div>
      <div>
        <div className={style['device-type']}>
          <FormattedMessage {...messages.scales} />
        </div>
        <ul className={style.list}>
          {!workstationScales.length ? (
            <div className={style['no-devices']}>
              <FormattedMessage {...messages.noScales} />
            </div>
          ) : (
            workstationScales.map((scale) => (
              <ConnectedDevice
                key={scale.id}
                currentUserWorkstationIds={currentUserWorkstationIds}
                device={scale}
                deviceType={getScaleType(scale)}
                hasAdminOrConfigPermissions={hasAdminOrConfigPermissions}
                openModal={openModal}
                userId={userId}
                workstationId={workstationId}
              />
            ))
          )}
        </ul>
      </div>
      <div>
        <div className={style['device-type']}>
          <FormattedMessage {...messages.printers} />
        </div>
        <ul className={style.list}>
          {!workstationPrinters.length ? (
            <div className={style['no-devices']}>
              <FormattedMessage {...messages.noPrinters} />
            </div>
          ) : (
            workstationPrinters.map((printer) => (
              <ConnectedDevice
                key={printer.id}
                currentUserWorkstationIds={currentUserWorkstationIds}
                device={printer}
                deviceType={PRINTER}
                hasAdminOrConfigPermissions={hasAdminOrConfigPermissions}
                openModal={openModal}
                userId={userId}
                workstationId={workstationId}
              />
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

ConnectedDevices.propTypes = {
  workstationId: PropTypes.string.isRequired,
  openModal: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  hasAdminOrConfigPermissions: PropTypes.bool,
  currentUserWorkstationIds: PropTypes.arrayOf(PropTypes.string),
};

export default ConnectedDevices;
